import { AssetDrawingsData, BaseFormData } from 'features/reporting/types'
import { GeckoMeasurement, GeckoMeasurementUnit, TimeString } from 'types'

export interface Resolution {
  height: number
  width: number
  widthUnit: string
  heightUnit: string
  angles?: number
}

export type SensorSerialNumbers = Record<number, string[]>

export interface DacData {
  serialNumber: string
  calibrationDate?: TimeString
}

export interface RobotData {
  model?: string
  serialNumber: string
  dacs?: DacData[]
}

export interface CalibrationBlock {
  type?: string
  serialNumber?: string
}

export interface CalibrationStep {
  stepName: string
  distance: {
    value: number
    unit: string
  }
}

export interface ComponentAxisStartLocation {
  xAxisStartLocation: string
  yAxisStartLocation: string
}

export const inspectionTemperatureUnits = [
  GeckoMeasurementUnit.Fahrenheit,
  GeckoMeasurementUnit.Celsius,
] as const
export const DEFAULT_INSPECTION_TEMPERATURE_UNIT =
  GeckoMeasurementUnit.Fahrenheit
export const DEFAULT_USE_TABLE_FOR_SUMMARY = true
export type InspectionTemperature = GeckoMeasurement | string

export interface MainInfo {
  assetName: string
  jobNumber: string
  assetType: string
  assetMaterial: string
  customerName: string
  customerLocation: string
  inspectionTemperature?: InspectionTemperature
  componentAxisStartLocations: Record<string, ComponentAxisStartLocation>
}

export interface CoverSectionData {
  inspectionStartDate: string
  inspectionEndDate: string
}

export interface SummarySectionData {
  inspectionResolution: Resolution
  deliverableResolution: Resolution
  coverageScope: string
  corrosionBLUF: string
  inspectionSummaryNotes?: string
  useTableForSummary?: boolean
  includeTMin?: boolean
}

export interface SensorDiameter {
  value: number
  unit: string
}

export interface SensorPayloadData {
  payloadType?: string
  sensorFrequency?: number
  sensorDiameter?: SensorDiameter
}

export interface DataSheetSectionData {
  sensorSerialNumbers: SensorSerialNumbers
  inspectionSurface: string
  surfaceCondition: string
  coatingPresent: boolean
  robots: RobotData[]
  calibrationBlocks: CalibrationBlock[]
  calibrationTimes: TimeString[]
  calibrationSteps: CalibrationStep[]
  procedure?: string
  procedureTitle?: string
  payloads?: SensorPayloadData[]
  inspectionSoftware?: string
}

export interface ImagesSectionData {
  imageReportIds: string[]
}

interface AddendumAttachmentFile {
  id: string
  path: string
  fileName: string
  caption?: string
}

export type SectionAddendumAttachment =
  | (AddendumAttachmentFile & { kind: 'image' })
  | (AddendumAttachmentFile & { kind: 'pdf'; pageCount: number })

export interface AddendumDraft {
  path: string
  fingerprint: string
}

export interface SectionAddendum {
  id?: string
  sectionId: string
  pageSubtitle: string
  addedPageCount: number
  isCustomSection?: boolean
  mode?: 'attachments' | 'blank'
  attachments?: SectionAddendumAttachment[]
}

export interface RugFormDataState extends Omit<
  BaseFormData,
  'lockState' | 'lock_state'
> {
  mainInfo: MainInfo
  coverSectionData: CoverSectionData
  summarySectionData: SummarySectionData
  dataSheetSectionData: DataSheetSectionData
  imagesSectionData: ImagesSectionData
  assetDrawings: AssetDrawingsData
  sectionAddendums: SectionAddendum[]
  addendumDraft?: AddendumDraft
}

export type RUGFieldSource = 'inspectionForm' | 'salesforce' | 'linkedJobData'
export type RUGFieldSources = Record<string, RUGFieldSource>

export enum RugReportSectionId {
  COVER = 'cover',
  INTRO_TO_RUG = 'introToRUG',
  SUMMARY = 'summary',
  DATA_SHEET = 'dataSheet',
  DATA_OVERVIEW = 'dataOverview',
  IMAGES = 'images',
  RUG_DATA_IMAGES = 'rugDataImages',
  REVIEW = 'review',
  TABLE_OF_CONTENTS = 'tableOfContents',
  SECTION_ADDENDUMS = 'sectionAddendums',
}

export type RUGPrePopulatedData = Partial<
  Pick<
    MainInfo,
    | 'assetName'
    | 'jobNumber'
    | 'assetType'
    | 'customerName'
    | 'customerLocation'
    | 'assetMaterial'
    | 'inspectionTemperature'
    | 'componentAxisStartLocations'
  > &
    CoverSectionData &
    SummarySectionData &
    DataSheetSectionData
>
